/**
 * The upsell surfaces, connected to `BookingsUpgradeContext`.
 *
 * `BookingsUpgradeModal` and `BookingsUpgradeSidebarCard` take plain props and
 * know nothing about eligibility, pricing or session rules — that keeps them
 * testable against a fixed set of inputs. These two wrappers are the only place
 * that reads the context, so the mount points (PageLayout, FlatSideMenu) stay
 * free of the feature's internals and simply render a self-gating leaf.
 */
import React from 'react';
import { BookingsUpgradeModal } from '../components/BookingsUpgradeModal';
import { BookingsUpgradeSidebarCard } from '../components/BookingsUpgradeSidebarCard';
import { useBookingsUpgrade } from '../contexts/BookingsUpgradeContext';

export function BookingsUpgradeModalContainer() {
  const {
    canShowDialog,
    isDialogOpen,
    usage,
    usageState,
    markDialogShown,
    closeDialog,
    goToCheckout,
  } = useBookingsUpgrade();

  // `usageState` is 'under' whenever there is nothing to say, so the two
  // states the dialog renders are the only ones that reach it.
  if (!canShowDialog || !usage || usageState === 'under') return null;

  return (
    <BookingsUpgradeModal
      isOpen={isDialogOpen}
      state={usageState}
      used={usage.used}
      limit={usage.limit}
      onClose={closeDialog}
      onUpgrade={goToCheckout}
      onShown={markDialogShown}
    />
  );
}

export function BookingsUpgradeSidebarCardContainer() {
  const { canShowCard, pricing, upgradeTarget, goToCheckout } =
    useBookingsUpgrade();

  // canShowCard already requires both; the second check is what tells
  // TypeScript so, and costs nothing.
  if (!canShowCard || !pricing || !upgradeTarget) return null;

  return (
    <BookingsUpgradeSidebarCard
      upgradeTarget={upgradeTarget}
      introductoryPrice={pricing.introductoryPrice}
      renewalPrice={pricing.renewalPrice}
      onUpgrade={goToCheckout}
    />
  );
}
